import React, { useCallback, useRef, useState } from 'react';
import type Autosuggest from 'react-autosuggest';

let chunk: Promise<typeof import('react-autosuggest')> | undefined;
const loadAutosuggest = () =>
    (chunk ??= import('react-autosuggest').catch((error) => {
        chunk = undefined;
        throw error;
    }));

// Callers pass a CSS module as the theme and type onChange with input events.
type Props = Omit<
    Autosuggest.AutosuggestProps<any, any>,
    'theme' | 'inputProps'
> & {
    theme?: Record<string, string | undefined>;
    inputProps: Omit<Autosuggest.InputProps<any>, 'onChange'> & {
        onChange: (event: any, params: Autosuggest.ChangeEvent) => void;
    };
};

const ITEMS_ID = 'react-autowhatever-1';

/**
 * react-autosuggest, loaded only in the browser the first time someone
 * hovers, touches or focuses the input. Until then a plain input with the
 * same markup and theme classes stands in, so the field is in the server
 * HTML, looks the same and accepts typing.
 */
const LazyAutoSuggest: React.FC<Props> = (props) => {
    const [ReactAutosuggest, setAutosuggest] = useState<
        typeof Autosuggest | null
    >(null);
    const placeholderRef = useRef<HTMLInputElement | null>(null);
    const selection = useRef<{
        start: number | null;
        end: number | null;
        direction: 'forward' | 'backward' | 'none' | null;
    } | null>(null);
    const loading = useRef(false);

    // Capture at removal, including typing/selection changes while React was
    // preparing the replacement, rather than when the download resolves.
    const setPlaceholder = useCallback((input: HTMLInputElement | null) => {
        const previous = placeholderRef.current;
        if (!input) {
            selection.current =
                previous && document.activeElement === previous
                    ? {
                          start: previous.selectionStart,
                          end: previous.selectionEnd,
                          direction: previous.selectionDirection,
                      }
                    : null;
        }
        placeholderRef.current = input;
    }, []);

    const load = () => {
        if (loading.current) return;
        loading.current = true;
        loadAutosuggest()
            .then(({ default: Component }) => {
                setAutosuggest(() => Component);
            })
            .catch(() => {
                // Keep the editable input; a later interaction can retry.
                loading.current = false;
            });
    };

    // Restore during the mount commit, after the real input exists and before
    // the browser can paint or dispatch another keystroke.
    const restoreFocus = useCallback((instance: Autosuggest | null) => {
        if (!instance?.input || !selection.current) return;
        const saved = selection.current;
        selection.current = null;
        const input = instance.input;
        if (
            input &&
            (document.activeElement === document.body ||
                document.activeElement === input)
        ) {
            input.focus();
            input.setSelectionRange(
                saved.start,
                saved.end,
                saved.direction ?? undefined
            );
        }
    }, []);

    const { theme = {}, inputProps } = props;
    // onBlur belongs to the real input, not the placeholder.
    const { onChange, onBlur: _onBlur, ...restInputProps } = inputProps;

    return (
        <div style={{ display: 'contents' }}>
            {ReactAutosuggest ? (
                <ReactAutosuggest {...(props as any)} ref={restoreFocus} />
            ) : (
                <div
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-owns={ITEMS_ID}
                    aria-expanded={false}
                    className={theme.container}
                >
                    <input
                        ref={setPlaceholder}
                        type="text"
                        autoComplete="off"
                        aria-autocomplete="list"
                        aria-controls={ITEMS_ID}
                        className={theme.input}
                        {...(restInputProps as React.InputHTMLAttributes<HTMLInputElement>)}
                        onChange={(e) =>
                            onChange(e, {
                                newValue: e.target.value,
                                method: 'type',
                            })
                        }
                        onPointerEnter={load}
                        onTouchStart={load}
                        onFocus={load}
                    />
                    <div
                        id={ITEMS_ID}
                        role="listbox"
                        className={theme.suggestionsContainer}
                    />
                </div>
            )}
        </div>
    );
};

export default LazyAutoSuggest;
